import React from "react";
import {
  Box,
  Divider,
  Grid,
  Link as MLink,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import Seo from "../../components/Seo/Seo";
import { PAGE_SEO } from "../../seo/seoConstants";
import Footer from "../../components/Footer/Footer";
import { SECTION_SX, CONTACT_EMAIL } from "../shared/constants";
import { CommonBoxStyles, AnchorSpan } from "../shared/styledComponents";

const Anchor: React.FC<{ id: string }> = ({ id }) => <AnchorSpan id={id} />;

export const ConditionsPage: React.FC = () => {
  return (
    <PageLayout>
      <Seo {...PAGE_SEO["/condiciones-del-servicio"]} />
      <CommonBoxStyles>
        <Box px={2}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item md={3} sx={{ display: { xs: "none", md: "block" } }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Contenido
                </Typography>
                <List dense>
                  {[
                    { href: "#aceptacion", label: "Aceptación" },
                    { href: "#ambito", label: "Ámbito de servicio" },
                    { href: "#cuentas", label: "Cuentas y autenticación" },
                    { href: "#compras", label: "Compras, pagos y notificaciones" },
                    { href: "#agendamiento", label: "Agendamiento y cancelaciones" },
                    { href: "#educativo", label: "Servicios educativos" },
                    { href: "#conducta", label: "Conducta prohibida" },
                    { href: "#propiedad", label: "Propiedad intelectual" },
                    { href: "#responsabilidad", label: "Limitación de responsabilidad" },
                    { href: "#garantias", label: "Garantías y disponibilidad" },
                    { href: "#modificaciones", label: "Modificaciones" },
                    { href: "#terminacion", label: "Terminación" },
                    { href: "#jurisdiccion", label: "Jurisdicción" },
                    { href: "#contacto", label: "Contacto" },
                  ].map((item) => (
                    <ListItem key={item.href} disablePadding>
                      <MLink
                        href={item.href}
                        underline="hover"
                        color="inherit"
                        sx={{ width: "100%" }}
                      >
                        <ListItemButton sx={{ borderRadius: 1 }}>
                          <ListItemText
                            primaryTypographyProps={{ variant: "body2" }}
                            primary={item.label}
                          />
                        </ListItemButton>
                      </MLink>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={9}>
              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  Condiciones del Servicio
                </Typography>
                <Typography
                  variant="body2"
                  fontStyle={"italic"}
                  color="text.primary"
                  sx={{ mt: 0.5 }}
                >
                  Última actualización: 8 de octubre de 2025
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Estas Condiciones regulan el acceso y uso de Entorno Biai (la «App») y aplican a
                  la compra de productos, la contratación y agendamiento de servicios online (p.
                  ej., reparación de celulares o computadores) y a las funcionalidades destinadas a
                  procesos educativos.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Anchor id="aceptacion" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  1. Aceptación
                </Typography>
                <Typography variant="body1">
                  Al usar la App aceptas estas Condiciones. Si no estás de acuerdo, no utilices la
                  App.
                </Typography>
                <Anchor id="ambito" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  2. Ámbito de servicio
                </Typography>
                <Typography variant="body1">
                  La App permite: (i) explorar productos; (ii) contratar servicios online,
                  incluyendo su agendamiento; y (iii) acceder a funcionalidades vinculadas a
                  procesos educativos. Algunas funciones pueden requerir registro y autenticación.
                </Typography>
                <Anchor id="cuentas" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  3. Cuentas y autenticación
                </Typography>
                <Typography variant="body1">
                  La autenticación se realiza mediante Google a través de Auth0. Eres responsable de
                  la confidencialidad de tus credenciales y de la actividad realizada en tu cuenta.
                  Notifícanos cualquier uso no autorizado.
                </Typography>
                <Anchor id="compras" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  4. Compras, pagos y notificaciones
                </Typography>
                <Typography variant="body1">
                  Cuando realizas una compra o contratas un servicio, podremos enviarte
                  notificaciones operativas (p. ej., confirmaciones, recordatorios o actualizaciones
                  de estado). Las condiciones comerciales específicas (precios, disponibilidades,
                  entregas o alcances) se mostrarán en la App antes de confirmar la compra o
                  contratación.
                </Typography>
                <Anchor id="agendamiento" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  5. Agendamiento y cancelaciones
                </Typography>
                <Typography variant="body1">
                  Puedes agendar servicios a través de la App. Las **cancelaciones** deberán
                  realizarse con al menos **2 días de anticipación** respecto a la fecha y hora
                  programadas. Las reprogramaciones y posibles cargos por cancelación tardía, si
                  aplican, se indicarán en el flujo de agendamiento o en las comunicaciones de
                  confirmación.
                </Typography>
                <Anchor id="educativo" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  6. Servicios educativos
                </Typography>
                <Typography variant="body1">
                  Podemos ofrecer funcionalidades para usuarios que llevan procesos educativos con
                  nosotros (p. ej., visualización de actividades, recordatorios y progreso). Los
                  detalles del servicio educativo se informarán en la App o en comunicaciones
                  asociadas.
                </Typography>
                <Anchor id="conducta" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  7. Conducta prohibida
                </Typography>
                <Typography variant="body1">
                  No debes utilizar la App de forma ilícita, que afecte derechos de terceros o que
                  interfiera con el funcionamiento del servicio (incluye intentos de acceso no
                  autorizado, inyección de código malicioso, suplantación de identidad o scraping no
                  permitido).
                </Typography>
                <Anchor id="propiedad" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  8. Propiedad intelectual
                </Typography>
                <Typography variant="body1">
                  El contenido disponible en la App (marcas, logotipos, textos, imágenes, código y
                  demás activos) es de Entorno Biai o de sus licenciantes y está protegido por la
                  legislación aplicable. No se otorgan licencias sobre dichos contenidos salvo
                  autorización expresa.
                </Typography>
                <Anchor id="responsabilidad" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  9. Limitación de responsabilidad
                </Typography>
                <Typography variant="body1">
                  En la máxima medida permitida por la ley, no seremos responsables por daños
                  indirectos, incidentales o consecuentes derivados del uso o imposibilidad de uso
                  de la App. Cuando la ley no permita excluir responsabilidad, esta se limitará al
                  máximo permitido.
                </Typography>
                <Anchor id="garantias" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  10. Garantías y disponibilidad
                </Typography>
                <Typography variant="body1">
                  La App se ofrece «tal cual» y sin garantías de disponibilidad continua. Podemos
                  realizar mantenimientos o actualizaciones que afecten temporalmente el servicio.
                </Typography>
                <Anchor id="modificaciones" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  11. Modificaciones
                </Typography>
                <Typography variant="body1">
                  Podemos actualizar estas Condiciones para reflejar cambios legales o funcionales.
                  Publicaremos la versión vigente en esta página e indicaremos la fecha de última
                  actualización. En caso de cambios sustanciales, podremos comunicarlo por los
                  medios disponibles.
                </Typography>
                <Anchor id="terminacion" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  12. Terminación
                </Typography>
                <Typography variant="body1">
                  Podemos suspender o cerrar cuentas que incumplan estas Condiciones o que
                  comprometan la seguridad y la operación de la App. También puedes solicitar la
                  eliminación de tu cuenta.
                </Typography>
                <Anchor id="jurisdiccion" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  13. Jurisdicción y ley aplicable
                </Typography>
                <Typography variant="body1">
                  Estas Condiciones se rigen por las leyes de la República de Colombia. Cualquier
                  disputa se someterá a los jueces competentes en Colombia, salvo normas imperativas
                  distintas.
                </Typography>
                <Anchor id="contacto" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  14. Contacto
                </Typography>
                <Typography variant="body1">
                  Si tienes preguntas sobre estas Condiciones, contáctanos en{" "}
                  <MLink href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                    {CONTACT_EMAIL}
                  </MLink>
                  .
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" color="text.primary">
                  Enlaces útiles:{" "}
                  <Link to="/politica-de-privacidad" style={{ textDecoration: "none" }}>
                    <MLink component="span" underline="hover">
                      Política de Privacidad
                    </MLink>
                  </Link>
                  {" • "}
                  <MLink
                    href="https://entornobiai.com"
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                  >
                    Página principal
                  </MLink>
                </Typography>
              </Paper>
            </Grid>
            <Footer>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} Entorno Biai
              </Typography>
            </Footer>
          </Grid>
        </Box>
      </CommonBoxStyles>
    </PageLayout>
  );
};

export default ConditionsPage;
